import React, { Component } from 'react';
import {
	Row, Col, Table, Input, Button, Tag, Divider, notification, Icon,
} from 'antd';
import { adminInstitutionSettingsService } from '../services';

// Kept as its own component (own state, own re-renders) instead of living
// inline in Institution.js: that page's render() also holds heavy widgets
// (Select with search, DatePicker, Upload, MaskedInput) that re-measure
// themselves on every re-render, so typing/saving/removing a setting in a
// shared render() made the whole page visibly jump.
class InstitutionSettingsPanel extends Component {
	state = {
		loading: false,
		settings: [],
		query: '',
		edits: {},
		saving: {},
		deleting: {},
	};

	componentDidMount() {
		this.load();
	}

	getErrorMessage = (error, fallback) => {
		const data = error && error.data ? error.data : null;
		if (typeof data === 'string') return data;
		return (data && data.message) || fallback;
	};

	load = () => {
		const { institutionId } = this.props;
		this.setState({ loading: true });
		adminInstitutionSettingsService
			.getInstitutionSettingsFor(institutionId)
			.then((settings) => {
				this.setState({ settings: settings || [], edits: {}, loading: false });
			})
			.catch((error) => {
				this.setState({ loading: false });
				notification.error({
					message: this.getErrorMessage(
						error,
						'Falha ao carregar as configurações da instituição',
					),
				});
			});
	};

	getEditedValue = (record) => {
		const { edits } = this.state;
		if (edits[record.institution_setting_id] !== undefined) {
			return edits[record.institution_setting_id];
		}
		return record.value === null || record.value === undefined ? '' : record.value;
	};

	isDirty = (record) => {
		const current = record.value === null || record.value === undefined ? '' : record.value;
		return this.getEditedValue(record) !== current;
	};

	handleSearchChange = (e) => {
		this.setState({ query: e.target.value });
	};

	handleValueChange = (record, value) => {
		this.setState((prevState) => ({
			edits: { ...prevState.edits, [record.institution_setting_id]: value },
		}));
	};

	handleSave = (record) => {
		const { institutionId } = this.props;
		const value = this.getEditedValue(record);
		const settingId = record.institution_setting_id;

		this.setState((prevState) => ({
			saving: { ...prevState.saving, [settingId]: true },
		}));

		adminInstitutionSettingsService
			.upsertInstitutionSettingValue(institutionId, settingId, value)
			.then((updated) => {
				this.setState((prevState) => {
					const settings = prevState.settings.map((setting) => {
						if (setting.institution_setting_id !== settingId) return setting;
						return { ...setting, ...updated };
					});
					const edits = { ...prevState.edits };
					delete edits[settingId];
					const saving = { ...prevState.saving };
					delete saving[settingId];
					return { settings, edits, saving };
				});
				notification.success({ message: 'Configuração salva com sucesso' });
			})
			.catch((error) => {
				this.setState((prevState) => {
					const saving = { ...prevState.saving };
					delete saving[settingId];
					return { saving };
				});
				notification.error({
					message: this.getErrorMessage(error, 'Falha ao salvar a configuração'),
				});
			});
	};

	handleRemove = (record) => {
		const { institutionId } = this.props;
		const settingId = record.institution_setting_id;

		this.setState((prevState) => ({
			deleting: { ...prevState.deleting, [settingId]: true },
		}));

		adminInstitutionSettingsService
			.deleteInstitutionSettingValue(institutionId, settingId)
			.then(() => {
				this.setState((prevState) => {
					const settings = prevState.settings.map((setting) => {
						if (setting.institution_setting_id !== settingId) return setting;
						return {
							...setting,
							id: null,
							value: setting.institution_setting.default_value,
							is_default: true,
						};
					});
					const edits = { ...prevState.edits };
					delete edits[settingId];
					const deleting = { ...prevState.deleting };
					delete deleting[settingId];
					return { settings, edits, deleting };
				});
				notification.success({ message: 'Configuração removida, valor padrão restaurado' });
			})
			.catch((error) => {
				this.setState((prevState) => {
					const deleting = { ...prevState.deleting };
					delete deleting[settingId];
					return { deleting };
				});
				notification.error({
					message: this.getErrorMessage(error, 'Falha ao remover a configuração'),
				});
			});
	};

	render() {
		const {
			settings, loading, query, saving, deleting,
		} = this.state;

		const q = (query || '').toLowerCase();
		const filteredSettings = q
			? settings.filter((setting) => {
				const name = (setting.institution_setting.name || '').toLowerCase();
				const description = (setting.institution_setting.description || '').toLowerCase();
				return name.includes(q) || description.includes(q);
			})
			: settings;

		const columns = [
			{
				title: 'Nome',
				key: 'name',
				render: (text, record) => record.institution_setting.name || '-',
			},
			{
				title: 'Descrição',
				key: 'description',
				render: (text, record) => record.institution_setting.description || '-',
			},
			{
				title: 'Valor',
				key: 'value',
				width: 340,
				render: (text, record) => (
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<Input
							style={{ width: 150 }}
							placeholder="Sem valor"
							value={this.getEditedValue(record)}
							onChange={(e) => this.handleValueChange(record, e.target.value)}
							onPressEnter={() => this.isDirty(record) && this.handleSave(record)}
							disabled={!!saving[record.institution_setting_id]}
						/>
						{record.is_default && record.value !== null && <Tag>Padrão</Tag>}
						<Button
							type="primary"
							size="small"
							style={{ width: 92, whiteSpace: 'nowrap' }}
							disabled={!this.isDirty(record) || !!saving[record.institution_setting_id]}
							onClick={() => this.handleSave(record)}
						>
							{saving[record.institution_setting_id] ? (
								<Icon type="loading" spin />
							) : (
								'Salvar'
							)}
						</Button>
						<Button
							size="small"
							style={{ width: 92, whiteSpace: 'nowrap' }}
							disabled={record.is_default || !!deleting[record.institution_setting_id]}
							onClick={() => this.handleRemove(record)}
						>
							{deleting[record.institution_setting_id] ? (
								<Icon type="loading" spin />
							) : (
								'Remover'
							)}
						</Button>
					</div>
				),
			},
		];

		return (
			<Row style={{ marginTop: 40 }}>
				<Col span={24}>
					<Divider style={{ background: '#bfbfbf' }} />
				</Col>
				<Col xs={24} lg={12}>
					<h1 className="title">Configurações da instituição</h1>
				</Col>
				<Col xs={24} lg={12}>
					<div style={{ marginBottom: 10 }}>
						<Input.Search
							placeholder="Buscar por nome ou descrição"
							value={query}
							onChange={this.handleSearchChange}
							allowClear
							style={{ width: '100%' }}
							disabled={loading}
						/>
					</div>
				</Col>
				<Col span={24}>
					<Table
						rowKey={(record) => record.institution_setting_id}
						loading={loading}
						dataSource={filteredSettings}
						columns={columns}
						pagination={false}
					/>
				</Col>
			</Row>
		);
	}
}

export default InstitutionSettingsPanel;
